import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  getTokens,
  storeTokens,
  clearTokens,
  isTokenExpired,
  parseJwt,
  buildAuthorizeUrl,
  refreshAccessToken,
  getEnvironment,
  getTesUrl,
  type OAuthTokens,
  type OAuthConfig,
} from '../lib/oauth';

export interface User {
  email: string;
  userId: string;
  roles: string[];
  permissions: string[];
  clientId: string;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  accessToken: string | null;
  error: string | null;
  login: () => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// OAuth configuration - uses current origin for redirect
const getOAuthConfig = (): OAuthConfig => {
  // Get the selected TES environment (lego-test or lego-live)
  const tesEnv = getEnvironment();

  // Use environment-based TES URL in production, or fallback to localhost for dev
  const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const tesUrl = isLocalDev
    ? (import.meta.env.VITE_TES_API_URL || 'http://localhost:8788')
    : getTesUrl(tesEnv);

  // Always use current origin for redirect to handle remote access
  const redirectUri = `${window.location.origin}/auth/callback`;

  return {
    authorizeUrl: `${tesUrl}/oauth/authorize`,
    // Use local proxy to avoid CORS issues with direct TES token endpoint
    tokenUrl: `${window.location.origin}/api/auth/token`,
    clientId: tesEnv, // Use environment as client ID
    redirectUri,
    scope: 'mcp:read mcp:write',
  };
};

function extractUserFromToken(token: string): User | null {
  const claims = parseJwt(token);
  if (!claims) return null;

  return {
    email: claims.email as string,
    userId: claims.userId as string,
    roles: (claims.roles as string[]) || [],
    permissions: (claims.permissions as string[]) || [],
    clientId: claims.clientId as string,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [tokens, setTokens] = useState<OAuthTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize from stored tokens
  useEffect(() => {
    const storedTokens = getTokens();
    if (storedTokens && !isTokenExpired(storedTokens)) {
      setTokens(storedTokens);
    } else if (storedTokens && storedTokens.refreshToken) {
      // Try to refresh expired token
      const config = getOAuthConfig();
      refreshAccessToken(config, storedTokens.refreshToken)
        .then((newTokens) => {
          setTokens(newTokens);
        })
        .catch(() => {
          clearTokens();
        })
        .finally(() => {
          setIsLoading(false);
        });
      return;
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async () => {
    try {
      setError(null);
      const config = getOAuthConfig();
      const url = await buildAuthorizeUrl(config);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    }
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setTokens(null);
    setError(null);
  }, []);

  const refreshTokenFn = useCallback(async () => {
    if (!tokens?.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      setError(null);
      const config = getOAuthConfig();
      const newTokens = await refreshAccessToken(config, tokens.refreshToken);
      setTokens(newTokens);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Token refresh failed');
      logout();
      throw err;
    }
  }, [tokens, logout]);

  // Handle token updates
  const handleTokenUpdate = useCallback((newTokens: OAuthTokens) => {
    storeTokens(newTokens);
    setTokens(newTokens);
    setIsLoading(false);
    setError(null);
  }, []);

  // Expose token update handler for callback component
  useEffect(() => {
    (window as unknown as { __authTokenUpdate?: (tokens: OAuthTokens) => void }).__authTokenUpdate = handleTokenUpdate;
    return () => {
      delete (window as unknown as { __authTokenUpdate?: (tokens: OAuthTokens) => void }).__authTokenUpdate;
    };
  }, [handleTokenUpdate]);

  const value = useMemo<AuthContextValue>(() => {
    const user = tokens?.accessToken ? extractUserFromToken(tokens.accessToken) : null;

    return {
      isAuthenticated: !!tokens && !isTokenExpired(tokens),
      isLoading,
      user,
      accessToken: tokens?.accessToken || null,
      error,
      login,
      logout,
      refreshToken: refreshTokenFn,
    };
  }, [tokens, isLoading, error, login, logout, refreshTokenFn]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext };
